/**
 * Smart Data Fetcher Service
 * Fetches only relevant data based on query analysis
 */

import { dataService, Order, Payment, RawMaterial, Customer } from './dataService';
import { queryClassifier, QueryAnalysis, QueryIntent } from './queryClassifier';
import { cacheService, CACHE_KEYS, CACHE_TTL } from './cacheService';

export interface SmartDataResponse {
  data: any;
  dataType: 'summary' | 'targeted' | 'full';
  entities: string[];
  timestamp: Date;
}

export class SmartDataFetcher {
  /**
   * Fetch relevant data based on query analysis
   */
  async fetchRelevantData(tenantId: string, query: string): Promise<SmartDataResponse> {
    const analysis = queryClassifier.analyzeQuery(query);
    
    // Check cache first
    const cacheKey = CACHE_KEYS.BUSINESS_DATA(tenantId);
    const cachedData = cacheService.get(cacheKey);
    
    if (cachedData && !queryClassifier.needsRealTimeData(analysis.intent)) {
      return {
        data: this.filterDataByIntent(cachedData, analysis),
        dataType: 'summary',
        entities: analysis.entities.customers.concat(analysis.entities.products),
        timestamp: new Date()
      };
    }

    // Fetch fresh data based on intent
    const data = await this.fetchDataByIntent(tenantId, analysis);
    
    // Cache the full business data
    cacheService.set(cacheKey, data, CACHE_TTL.BUSINESS_DATA);
    
    return {
      data: this.filterDataByIntent(data, analysis),
      dataType: 'targeted',
      entities: analysis.entities.customers.concat(analysis.entities.products),
      timestamp: new Date()
    };
  }

  /**
   * Fetch data based on query intent
   */
  private async fetchDataByIntent(tenantId: string, analysis: QueryAnalysis): Promise<any> {
    const requirements = queryClassifier.getDataRequirements(analysis.intent);
    
    // Fetch only required data types
    const fetchPromises = requirements.map(type => this.fetchDataType(tenantId, type));
    const results = await Promise.allSettled(fetchPromises);
    
    const data: any = {};
    
    // Process results
    results.forEach((result, index) => {
      const dataType = requirements[index];
      if (result.status === 'fulfilled') {
        data[dataType] = result.value;
      } else {
        data[dataType] = [];
      }
    });

    // Add computed metrics
    data.metrics = await this.computeMetrics(data, analysis.intent);
    
    return data;
  }

  /**
   * Fetch specific data type
   */
  private async fetchDataType(tenantId: string, dataType: string): Promise<any[]> {
    switch (dataType) {
      case 'orders':
        return await dataService.getOrders(tenantId);
      case 'customers':
        return await dataService.getCustomers(tenantId);
      case 'menuItems':
        return await dataService.getMenuItems(tenantId);
      case 'rawMaterials':
        return await dataService.getRawMaterials(tenantId);
      case 'payments':
        return await dataService.getPayments(tenantId);
      case 'inventoryTransactions':
        return await dataService.getInventoryTransactions(tenantId);
      default:
        return [];
    }
  }

  /**
   * Filter data based on query intent
   */
  private filterDataByIntent(data: any, analysis: QueryAnalysis): any {
    const filteredData = { ...data };

    switch (analysis.intent) {
      case 'sales':
        return this.filterSalesData(filteredData, analysis);
      case 'customers':
        return this.filterCustomerData(filteredData, analysis);
      case 'inventory':
        return this.filterInventoryData(filteredData, analysis);
      case 'payments':
        return this.filterPaymentData(filteredData, analysis);
      case 'orders':
        return this.filterOrderData(filteredData, analysis);
      default:
        return this.filterGeneralData(filteredData, analysis);
    }
  }

  /**
   * Filter data for sales queries
   */
  private filterSalesData(data: any, analysis: QueryAnalysis): any {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Filter orders by date if specified
    let orders = data.orders || [];
    if (analysis.entities.dates.includes('today')) {
      orders = orders.filter((order: Order) => this.isToday(order.createdAt));
    } else if (analysis.entities.dates.includes('yesterday')) {
      orders = orders.filter((order: Order) => this.isYesterday(order.createdAt));
    }

    // Limit to recent orders for performance
    orders = orders.slice(0, 50);

    return {
      orders,
      metrics: data.metrics,
      popularItems: this.getPopularItems(orders).slice(0, 10),
      salesTrends: this.getSalesTrends(orders)
    };
  }

  /**
   * Filter data for customer queries
   */
  private filterCustomerData(data: any, analysis: QueryAnalysis): any {
    let customers = data.customers || [];
    
    // Filter by specific customers if mentioned
    if (analysis.entities.customers.length > 0) {
      customers = customers.filter((customer: Customer) =>
        analysis.entities.customers.some(name =>
          customer.name.toLowerCase().includes(name.toLowerCase())
        )
      );
    }

    // Sort by spending for top customers
    customers = customers
      .sort((a: Customer, b: Customer) => (b.totalSpent || 0) - (a.totalSpent || 0))
      .slice(0, 20);

    return {
      customers,
      topCustomers: customers.slice(0, 10),
      customerMetrics: this.getCustomerMetrics(customers)
    };
  }

  /**
   * Filter data for inventory queries
   */
  private filterInventoryData(data: any, analysis: QueryAnalysis): any {
    let rawMaterials = data.rawMaterials || [];
    
    // Filter by specific products if mentioned
    if (analysis.entities.products.length > 0) {
      rawMaterials = rawMaterials.filter((material: RawMaterial) =>
        analysis.entities.products.some(product =>
          material.name.toLowerCase().includes(product.toLowerCase())
        )
      );
    }

    // Focus on low stock items
    const lowStockItems = rawMaterials.filter((material: RawMaterial) =>
      material.currentStock <= material.minStock
    );

    return {
      rawMaterials,
      lowStockItems,
      inventoryValue: this.calculateInventoryValue(rawMaterials),
      stockAlerts: this.generateStockAlerts(lowStockItems)
    };
  }

  /**
   * Filter data for payment queries
   */
  private filterPaymentData(data: any, analysis: QueryAnalysis): any {
    const orders = data.orders || [];
    const payments = data.payments || [];

    // Focus on unpaid orders
    const unpaidOrders = orders.filter((order: Order) =>
      !order.isPaid || (order.paidAmount || 0) < order.total
    );

    // Group by customer
    const unpaidByCustomer = this.groupUnpaidByCustomer(unpaidOrders);

    return {
      unpaidOrders,
      unpaidByCustomer,
      totalUnpaidAmount: unpaidOrders.reduce((sum: number, order: Order) =>
        sum + (order.total - (order.paidAmount || 0)), 0
      ),
      paymentMetrics: this.getPaymentMetrics(payments)
    };
  }

  /**
   * Filter data for order queries
   */
  private filterOrderData(data: any, analysis: QueryAnalysis): any {
    let orders = data.orders || [];

    // Filter by specific orders if mentioned
    if (analysis.entities.orders.length > 0) {
      orders = orders.filter((order: Order) =>
        analysis.entities.orders.some(orderId =>
          order.id?.toLowerCase().includes(orderId.toLowerCase())
        )
      );
    }

    // Limit to recent orders
    orders = orders.slice(0, 100);

    return {
      orders,
      orderMetrics: this.getOrderMetrics(orders),
      recentOrders: orders.slice(0, 20)
    };
  }

  /**
   * Filter data for general queries
   */
  private filterGeneralData(data: any, analysis: QueryAnalysis): any {
    return {
      summary: {
        totalOrders: data.orders?.length || 0,
        totalCustomers: data.customers?.length || 0,
        totalRevenue: data.metrics?.totalRevenue || 0,
        lowStockCount: data.rawMaterials?.filter((r: RawMaterial) =>
          r.currentStock <= r.minStock
        ).length || 0
      },
      metrics: data.metrics,
      topItems: this.getPopularItems(data.orders || []).slice(0, 5),
      topCustomers: (data.customers || [])
        .sort((a: Customer, b: Customer) => (b.totalSpent || 0) - (a.totalSpent || 0))
        .slice(0, 5)
    };
  }

  /**
   * Compute metrics for specific intent
   */
  private async computeMetrics(data: any, intent: QueryIntent): Promise<any> {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const orders = data.orders || [];

    const todayOrders = orders.filter((order: Order) => this.isToday(order.createdAt));
    const yesterdayOrders = orders.filter((order: Order) => this.isYesterday(order.createdAt));

    return {
      todaySales: todayOrders.reduce((sum: number, order: Order) => sum + (order.total || 0), 0),
      todayOrders: todayOrders.length,
      yesterdaySales: yesterdayOrders.reduce((sum: number, order: Order) => sum + (order.total || 0), 0),
      yesterdayOrders: yesterdayOrders.length,
      totalRevenue: orders.reduce((sum: number, order: Order) => sum + (order.total || 0), 0),
      averageOrderValue: orders.length > 0 ? 
        orders.reduce((sum: number, order: Order) => sum + (order.total || 0), 0) / orders.length : 0
    };
  }

  // Helper methods
  private isToday(date: any): boolean {
    if (!date) return false;
    const today = new Date();
    const orderDate = new Date(date);
    return orderDate.toDateString() === today.toDateString();
  }

  private isYesterday(date: any): boolean {
    if (!date) return false;
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const orderDate = new Date(date);
    return orderDate.toDateString() === yesterday.toDateString();
  }

  private getPopularItems(orders: Order[]): Array<{name: string, quantity: number, revenue: number}> {
    const itemSales = new Map<string, { quantity: number; revenue: number }>();
    
    orders.forEach(order => {
      order.items?.forEach(item => {
        const existing = itemSales.get(item.menuItemName) || { quantity: 0, revenue: 0 };
        itemSales.set(item.menuItemName, {
          quantity: existing.quantity + (item.quantity || 0),
          revenue: existing.revenue + (item.totalPrice || 0)
        });
      });
    });

    return Array.from(itemSales.entries())
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.revenue - a.revenue);
  }

  private getSalesTrends(orders: Order[]): Array<{date: string, revenue: number}> {
    const trends: Array<{date: string, revenue: number}> = [];
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return date;
    });

    last7Days.forEach(date => {
      const dayOrders = orders.filter(order => {
        const orderDate = new Date(order.createdAt || 0);
        return orderDate.toDateString() === date.toDateString();
      });

      trends.push({
        date: date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }),
        revenue: dayOrders.reduce((sum, order) => sum + (order.total || 0), 0)
      });
    });

    return trends.reverse();
  }

  private getCustomerMetrics(customers: Customer[]): any {
    return {
      totalCustomers: customers.length,
      activeCustomers: customers.filter(c => c.status === 'active').length,
      averageSpending: customers.length > 0 ?
        customers.reduce((sum, c) => sum + (c.totalSpent || 0), 0) / customers.length : 0
    };
  }

  private calculateInventoryValue(rawMaterials: RawMaterial[]): number {
    return rawMaterials.reduce((sum, material) =>
      sum + (material.currentStock * (material.costPerSubUnit || material.costPerUnit || 0)), 0
    );
  }

  private generateStockAlerts(lowStockItems: RawMaterial[]): Array<{item: string, current: number, min: number}> {
    return lowStockItems.map(item => ({
      item: item.name,
      current: item.currentStock,
      min: item.minStock
    }));
  }

  private groupUnpaidByCustomer(unpaidOrders: Order[]): Array<{customer: string, amount: number, orders: number}> {
    const customerMap = new Map<string, { amount: number; orders: number }>();
    
    unpaidOrders.forEach(order => {
      const existing = customerMap.get(order.customer) || { amount: 0, orders: 0 };
      customerMap.set(order.customer, {
        amount: existing.amount + (order.total - (order.paidAmount || 0)),
        orders: existing.orders + 1
      });
    });

    return Array.from(customerMap.entries())
      .map(([customer, data]) => ({ customer, ...data }))
      .sort((a, b) => b.amount - a.amount);
  }

  private getPaymentMetrics(payments: Payment[]): any {
    return {
      totalPayments: payments.length,
      totalAmount: payments.reduce((sum, p) => sum + (p.amount || 0), 0),
      averagePayment: payments.length > 0 ?
        payments.reduce((sum, p) => sum + (p.amount || 0), 0) / payments.length : 0
    };
  }

  private getOrderMetrics(orders: Order[]): any {
    return {
      totalOrders: orders.length,
      completedOrders: orders.filter(o => o.status === 'completed').length,
      activeOrders: orders.filter(o => o.status === 'active').length,
      averageOrderValue: orders.length > 0 ?
        orders.reduce((sum, o) => sum + (o.total || 0), 0) / orders.length : 0
    };
  }
}

// Singleton instance
export const smartDataFetcher = new SmartDataFetcher();
